import { useEffect, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import s from "./Projects.module.css";
import { useCreateProject, useProjectVocabulary, useSetProjectSchedule, useUpdateProject } from "../../api/queries";
import {
  fromMinor,
  PROJECT_STATUS_LABEL,
  PROJECT_STATUSES,
  toMinor,
  type ProjectDetail,
  type ProjectStatus,
  type UpdateProjectInput,
} from "../../domain/projects";
import { Button, Field, Input, Select } from "../../design/components";
import { t, tn } from "../../i18n";

const dialogStyle: CSSProperties = {
  background: "var(--panel)",
  color: "var(--ink)",
  border: "1px solid var(--line)",
  borderRadius: "var(--radius)",
  padding: 20,
  width: "min(640px, 94vw)",
};

const textareaStyle: CSSProperties = {
  padding: "6px 10px",
  border: "1px solid var(--line)",
  borderRadius: "var(--radius-sm)",
  background: "var(--panel2)",
  color: "var(--ink)",
  font: "inherit",
  fontSize: 13.5,
  width: "100%",
  boxSizing: "border-box",
};

function Modal({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    // No close() on cleanup. StrictMode runs this cleanup between its two
    // mounts in development, and the close event it fires unmounted the
    // dialog as it opened — the button did nothing. An unmounted dialog
    // leaves the top layer by itself.
  }, []);
  return (
    <dialog ref={ref} onClose={onClose} style={dialogStyle} aria-label={label}>
      {children}
    </dialog>
  );
}

function Refusal({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <div className={s.bad} role="alert" style={{ gridColumn: "1 / -1" }}>
      {t("Refused: {message}", { message: error instanceof Error ? error.message : String(error) })}
    </div>
  );
}

/** A money figure typed in the currency's major unit — "1,250,000" — stored as minor units. */
function MoneyField({
  label,
  value,
  onChange,
  currency,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  currency: string;
}) {
  const bad = Number.isNaN(toMinor(value, currency));
  return (
    <Field label={t("{label} ({currency})", { label: t(label), currency })}>
      <Input
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t("not declared")}
        aria-invalid={bad}
        style={bad ? { borderColor: "var(--red)" } : undefined}
      />
    </Field>
  );
}

/**
 * Raise a capital project.
 *
 * The category and the gate set are the packs' vocabulary, not free text: a
 * project against a gate set nobody declares is refused by the platform, since
 * a project with no gates reads everywhere as one that has passed none. A
 * blank budget is sent as nothing — "not declared" — never as zero.
 */
export function NewProjectDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const vocab = useProjectVocabulary();
  const create = useCreateProject();
  const [f, setF] = useState({
    name: "",
    category: "",
    gateSet: "",
    code: "",
    status: "planned" as ProjectStatus,
    description: "",
    startDate: "",
    endDate: "",
    baselineStart: "",
    baselineEnd: "",
    budget: "",
    contractor: "",
    consultant: "",
  });
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));
  const currency = "SAR";
  const budgetMinor = toMinor(f.budget, currency);
  const categories = vocab.data?.categories ?? [];
  const category = categories.find((c) => c.key === f.category);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!f.name.trim() || !f.category || Number.isNaN(budgetMinor)) return;
    create.mutate(
      {
        name: f.name.trim(),
        category: f.category,
        gateSet: f.gateSet || undefined,
        code: f.code.trim() || undefined,
        status: f.status,
        description: f.description.trim() || undefined,
        startDate: f.startDate || undefined,
        endDate: f.endDate || undefined,
        baselineStart: f.baselineStart || undefined,
        baselineEnd: f.baselineEnd || undefined,
        budgetMinor,
        currency,
        contractor: f.contractor.trim() || undefined,
        consultant: f.consultant.trim() || undefined,
      },
      { onSuccess: (r) => onCreated(r.id) },
    );
  };

  return (
    <Modal label={t("New capital project")} onClose={onClose}>
      <form onSubmit={submit} className={s.form}>
        <h2 className={s.formWide}>{t("New capital project")}</h2>
        <div className={s.formWide}>
          <Field label="Name">
            <Input value={f.name} onChange={(e) => set("name")(e.target.value)} required autoFocus />
          </Field>
        </div>
        <Field label="Category">
          <Select value={f.category} onChange={(e) => set("category")(e.target.value)} required>
            <option value="">{t("Choose…")}</option>
            {categories.map((c) => (
              <option key={c.key} value={c.key}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Gate set">
          <Select value={f.gateSet} onChange={(e) => set("gateSet")(e.target.value)}>
            <option value="">
              {category?.defaultGateSet
                ? t("Category default ({gateSet})", { gateSet: category.defaultGateSet })
                : t("Category default")}
            </option>
            {(vocab.data?.gateSets ?? []).map((g) => (
              <option key={g.key} value={g.key}>
                {t("{name} — {n} gates", { name: g.name, n: g.gates.length })}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Code (optional)">
          <Input value={f.code} onChange={(e) => set("code")(e.target.value)} placeholder={t("assigned if blank")} />
        </Field>
        <Field label="Status">
          <Select value={f.status} onChange={(e) => set("status")(e.target.value)}>
            {PROJECT_STATUSES.map((st) => (
              <option key={st} value={st}>
                {t(PROJECT_STATUS_LABEL[st])}
              </option>
            ))}
          </Select>
        </Field>
        <div className={s.formWide}>
          <Field label="Description">
            <textarea
              value={f.description}
              onChange={(e) => set("description")(e.target.value)}
              rows={3}
              style={textareaStyle}
            />
          </Field>
        </div>
        <Field label="Start">
          <Input type="date" value={f.startDate} onChange={(e) => set("startDate")(e.target.value)} />
        </Field>
        <Field label="End">
          <Input
            type="date"
            value={f.endDate}
            min={f.startDate || undefined}
            onChange={(e) => set("endDate")(e.target.value)}
          />
        </Field>
        <Field label="Baseline start">
          <Input type="date" value={f.baselineStart} onChange={(e) => set("baselineStart")(e.target.value)} />
        </Field>
        <Field label="Baseline end">
          <Input
            type="date"
            value={f.baselineEnd}
            min={f.baselineStart || undefined}
            onChange={(e) => set("baselineEnd")(e.target.value)}
          />
        </Field>
        <div className={`${s.formWide} ${s.dialogNote}`}>
          {t(
            "The baseline is what every slip is measured against. Setting it now is free; moving it later needs a reason, which is recorded.",
          )}
        </div>
        <MoneyField label="Budget" value={f.budget} onChange={set("budget")} currency={currency} />
        <Field label="Contractor">
          <Input value={f.contractor} onChange={(e) => set("contractor")(e.target.value)} />
        </Field>
        <Field label="Consultant">
          <Input value={f.consultant} onChange={(e) => set("consultant")(e.target.value)} />
        </Field>
        <Refusal error={create.error} />
        <div className={s.formFoot}>
          <Button onClick={onClose} disabled={create.isPending}>
            {t("Cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={create.isPending || !f.name.trim() || !f.category || Number.isNaN(budgetMinor)}
          >
            {create.isPending ? t("Creating…") : t("Create project")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Change a project's details and money.
 *
 * Only what was changed is sent, because every field sent is audited as a
 * change. Committed and spent are entered as given — the platform computes no
 * variance from them and neither does this form.
 */
export function EditProjectDialog({ project: p, onClose }: { project: ProjectDetail; onClose: () => void }) {
  const vocab = useProjectVocabulary();
  const update = useUpdateProject(p.id);
  const currency = p.money.currency ?? "SAR";
  const initial = {
    name: p.name,
    status: p.status,
    category: p.category ?? "",
    description: p.description ?? "",
    budget: fromMinor(p.money.budgetMinor, currency),
    committed: fromMinor(p.money.committedMinor, currency),
    spent: fromMinor(p.money.spentMinor, currency),
    contractor: p.contractor ?? "",
    consultant: p.consultant ?? "",
  };
  const [f, setF] = useState(initial);
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));
  const money = {
    budget: toMinor(f.budget, currency),
    committed: toMinor(f.committed, currency),
    spent: toMinor(f.spent, currency),
  };
  const invalid = Object.values(money).some((m) => Number.isNaN(m));

  const changes: UpdateProjectInput = {};
  if (f.name.trim() && f.name.trim() !== initial.name) changes.name = f.name.trim();
  if (f.status !== initial.status) changes.status = f.status;
  if (f.category && f.category !== initial.category) changes.category = f.category;
  if (f.description.trim() !== initial.description && f.description.trim()) changes.description = f.description.trim();
  if (f.contractor.trim() !== initial.contractor && f.contractor.trim()) changes.contractor = f.contractor.trim();
  if (f.consultant.trim() !== initial.consultant && f.consultant.trim()) changes.consultant = f.consultant.trim();
  if (f.budget !== initial.budget && money.budget != null) changes.budgetMinor = money.budget;
  if (f.committed !== initial.committed && money.committed != null) changes.committedMinor = money.committed;
  if (f.spent !== initial.spent && money.spent != null) changes.spentMinor = money.spent;
  const count = Object.keys(changes).length;

  return (
    <Modal label={t("Edit {code}", { code: p.code })} onClose={onClose}>
      <form
        className={s.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (!count || invalid) return;
          update.mutate(changes, { onSuccess: onClose });
        }}
      >
        <h2 className={s.formWide}>
          {t("Edit")} <span className="mono">{p.code}</span>
        </h2>
        <div className={s.formWide}>
          <Field label="Name">
            <Input value={f.name} onChange={(e) => set("name")(e.target.value)} required />
          </Field>
        </div>
        <Field label="Status">
          <Select value={f.status} onChange={(e) => set("status")(e.target.value)}>
            {PROJECT_STATUSES.map((st) => (
              <option key={st} value={st}>
                {t(PROJECT_STATUS_LABEL[st])}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Category">
          <Select value={f.category} onChange={(e) => set("category")(e.target.value)}>
            {!f.category && <option value="">—</option>}
            {(vocab.data?.categories ?? []).map((c) => (
              <option key={c.key} value={c.key}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <div className={s.formWide}>
          <Field label="Description">
            <textarea
              value={f.description}
              onChange={(e) => set("description")(e.target.value)}
              rows={3}
              style={textareaStyle}
            />
          </Field>
        </div>
        <MoneyField label="Budget" value={f.budget} onChange={set("budget")} currency={currency} />
        <MoneyField label="Committed" value={f.committed} onChange={set("committed")} currency={currency} />
        <MoneyField label="Spent" value={f.spent} onChange={set("spent")} currency={currency} />
        <div className={s.dialogNote} style={{ alignSelf: "end" }}>
          {t("As declared. No variance or percentage is computed from these.")}
        </div>
        <Field label="Contractor">
          <Input value={f.contractor} onChange={(e) => set("contractor")(e.target.value)} />
        </Field>
        <Field label="Consultant">
          <Input value={f.consultant} onChange={(e) => set("consultant")(e.target.value)} />
        </Field>
        <div className={`${s.formWide} ${s.dialogNote}`}>
          {t("Every change is recorded in the audit trail with the value it replaced. Dates are set under Schedule.")}
        </div>
        <Refusal error={update.error} />
        <div className={s.formFoot}>
          <Button onClick={onClose} disabled={update.isPending}>
            {t("Cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={update.isPending || !count || invalid}>
            {update.isPending
              ? t("Saving…")
              : count
                ? tn(count, "Save {n} change", "Save {n} changes")
                : t("No changes")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Current dates, and the baseline.
 *
 * Once a baseline exists, moving it asks for a reason before the button
 * enables — the platform refuses without one, and says why: every slip is
 * measured against the baseline, so moving it silently reports a late project
 * as on time.
 */
export function ScheduleDialog({ project: p, onClose }: { project: ProjectDetail; onClose: () => void }) {
  const save = useSetProjectSchedule(p.id);
  const sc = p.schedule;
  const [f, setF] = useState({
    startDate: sc.startDate?.slice(0, 10) ?? "",
    endDate: sc.endDate?.slice(0, 10) ?? "",
    baselineStart: sc.baselineStart?.slice(0, 10) ?? "",
    baselineEnd: sc.baselineEnd?.slice(0, 10) ?? "",
    reason: "",
  });
  const [warnings, setWarnings] = useState<string[]>([]);
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));
  const hadBaseline = Boolean(sc.baselineStart || sc.baselineEnd);
  const moving =
    (f.baselineStart && f.baselineStart !== (sc.baselineStart?.slice(0, 10) ?? "")) ||
    (f.baselineEnd && f.baselineEnd !== (sc.baselineEnd?.slice(0, 10) ?? ""));
  const needsReason = Boolean(hadBaseline && moving);

  return (
    <Modal label={t("Schedule for {code}", { code: p.code })} onClose={onClose}>
      <form
        className={s.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (needsReason && !f.reason.trim()) return;
          save.mutate(
            {
              startDate: f.startDate || undefined,
              endDate: f.endDate || undefined,
              baselineStart: f.baselineStart || undefined,
              baselineEnd: f.baselineEnd || undefined,
              rebaselineReason: needsReason ? f.reason.trim() : undefined,
            },
            {
              onSuccess: (r) => {
                if (r.warnings.length) setWarnings(r.warnings);
                else onClose();
              },
            },
          );
        }}
      >
        <h2 className={s.formWide}>
          {t("Schedule —")} <span className="mono">{p.code}</span>
        </h2>
        <Field label="Start">
          <Input type="date" value={f.startDate} onChange={(e) => set("startDate")(e.target.value)} />
        </Field>
        <Field label="End">
          <Input
            type="date"
            value={f.endDate}
            min={f.startDate || undefined}
            onChange={(e) => set("endDate")(e.target.value)}
          />
        </Field>
        <Field label="Baseline start">
          <Input type="date" value={f.baselineStart} onChange={(e) => set("baselineStart")(e.target.value)} />
        </Field>
        <Field label="Baseline end">
          <Input
            type="date"
            value={f.baselineEnd}
            min={f.baselineStart || undefined}
            onChange={(e) => set("baselineEnd")(e.target.value)}
          />
        </Field>
        {needsReason && (
          <div className={s.formWide}>
            <Field label="Reason for moving the baseline (required, recorded)">
              <Input
                value={f.reason}
                onChange={(e) => set("reason")(e.target.value)}
                required
                placeholder={t("e.g. scope added by change order 3")}
              />
            </Field>
            <div className={s.warn}>
              {tn(
                sc.rebaselined,
                "This project has been rebaselined {n} time. Slip will be measured against the new baseline; the old one stays on the record.",
                "This project has been rebaselined {n} times. Slip will be measured against the new baseline; the old one stays on the record.",
              )}
            </div>
          </div>
        )}
        {warnings.map((w) => (
          <div key={w} className={`${s.formWide} ${s.warn}`} role="status">
            {t("Saved.")} {w}
          </div>
        ))}
        <Refusal error={save.error} />
        <div className={s.formFoot}>
          <Button onClick={onClose} disabled={save.isPending}>
            {warnings.length ? t("Close") : t("Cancel")}
          </Button>
          {!warnings.length && (
            <Button type="submit" variant="primary" disabled={save.isPending || (needsReason && !f.reason.trim())}>
              {save.isPending ? t("Saving…") : needsReason ? t("Move the baseline") : t("Save dates")}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
}
